import React, { useState, useEffect } from 'react';
import UserInfoModal from './components/UserInfoModal/UserInfoModal';
import ProgressBar from './components/ProgressBar/ProgressBar';
import Question from './components/Question/Question';
import Recommendation from './components/Recommendation/Recommendation';
import LoginModal from './components/LoginModal/LoginModal';
import RegisterModal from './components/RegisterModal/RegisterModal';
import PreviousQuotesModal from './components/PreviousQuotesModal/PreviousQuotesModal';
import AIAssistant from './components/AIAssistant/AIAssistant';
import AIModal from './components/AIModal/AIModal';
import Questions from './components/Questions/Questions';
import axios from 'axios';
import './index.css';
import pricingData from './pricing.json'; //Import pricing data
import NavBar from './components/NavBar/NavBar';
import AdminModal from './components/AdminModal/AdminModal';

const BASE_URL = process.env.NODE_ENV === 'production'
    ? (process.env.REACT_APP_IS_BETA === 'true' ? 'https://betaquotes.omntec.com' : 'https://quotes.omntec.com')
    : window.location.protocol === 'https:'
        ? 'https://localhost:3001'
        : 'http://localhost:3001';


// Configure axios defaults
axios.defaults.withCredentials = true;
axios.defaults.headers.common['Content-Type'] = 'application/json';
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

function OmntecQuoteGenerator()
{
    const [answers, setAnswers] = useState({});
    const [initialRecommendation, setInitialRecommendation] = useState(null);
    const [recommendation, setRecommendation] = useState(null);
    const [filteredSections, setFilteredSections] = useState([]);
    const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
    const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
    const [userInfo, setUserInfo] = useState({
        email: '',
        name: '',
        company: '',
        phoneNumber: '',
        projectName: '',
        city: '',
        state: ''
    });
    const [showModal, setShowModal] = useState(true);
    const [initialStep, setInitialStep] = useState(true);
    const [loggedIn, setLoggedIn] = useState(false);
    const [showRegisterModal, setShowRegisterModal] = useState(false);
    const [user, setUser] = useState(null);
    const [quotes, setQuotes] = useState([]);
    const [showQuotesModal, setShowQuotesModal] = useState(false);
    const [currentQuoteNumber, setCurrentQuoteNumber] = useState(null);
    const [aiModalOpen, setAiModalOpen] = useState(false);
    // Declare state variables for total sensors and individual sensor counts
    //const [totalLeakSensors, setTotalLeakSensors] = useState(0);
    //const [sumpSensors, setSumpSensors] = useState(0);
    //const [dispenserSensors, setDispenserSensors] = useState(0);
    //const [intersticeSensors, setIntersticeSensors] = useState(0);
    const [dropdownSearchTerm, setDropdownSearchTerm] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [items, setItems] = useState([]);
    const [totalPrice, setTotalPrice] = useState(0);
    const [showAdminModal, setShowAdminModal] = useState(false);
    //const [quoteValidUntil, setQuoteValidUntil] = useState(null);
    const [quoteCreatedAt, setQuoteCreatedAt] = useState(null);
    const [userDiscount, setUserDiscount] = useState(0);
    const [currentModel, setCurrentModel] = useState(null);
    const [hardStopMessage, setHardStopMessage] = useState(null);

    // Load user discount from database when user logs in
    useEffect(() => {
        if (user) {
            axios.get(`${BASE_URL}/api/users/${user.id}/discount`)
                .then(response => {
                    if (response.data.success) {
                        setUserDiscount(parseFloat(response.data.discount));
                    }
                })
                .catch(error => console.error('Error loading user discount:', error));
        }
    }, [user]);

    // Save discount to database when it changes
    const updateUserDiscount = async (newDiscount) => {
        try {
            const response = await axios.post(`${BASE_URL}/api/users/${user.id}/discount`, {
                discount: newDiscount
            });
            if (response.data.success) {
                setUserDiscount(newDiscount);
            }
        } catch (error) {
            console.error('Error saving user discount:', error);
        }
    };

    useEffect(() => 
    {
        setFilteredSections(Questions.getFilteredSections());
    }, []);

    useEffect(() => 
    {
        if (user) 
        {
            fetchQuotes();
        }
    }, [user]);

    useEffect(() =>
    {
        if (!initialStep && currentQuestionIndex === 0) 
        {
            const recommendation = Questions.determineInitialRecommendation(answers);
    
            if (recommendation && recommendation.length > 0) 
            {
                setInitialRecommendation(recommendation);
            } 
            else 
            {
                //console.error("No valid recommendation could be determined.");
                setInitialRecommendation("OEL8000IIIK");
            }
        }
    }, [answers]);  //This will run every time `answers` updates

    // Add this useEffect to check auth status on load
    useEffect(() => {
        const verifyAuth = async () => {
            try {
                const response = await axios.get(
                    `${BASE_URL}/api/verify-token`,
                    {
                        withCredentials: true 
                    }
                );
                if (response.data.success) {
                    setUser(response.data.user);
                    setLoggedIn(true);
                    // Set user discount from the verified user data
                    if (response.data.user.discount !== undefined) {
                        setUserDiscount(parseFloat(response.data.user.discount) || 0);
                    }
                }
            } catch (error) {
                console.error('Auth verification failed:', error);
            }
        };

        verifyAuth();
    }, []);

    // Check for quote URL parameter and load that quote
    useEffect(() => {
        const loadQuoteFromUrl = async () => {
            const urlParams = new URLSearchParams(window.location.search);
            const quoteNumber = urlParams.get('quote');
            
            if (quoteNumber && loggedIn) {
                try {
                    const response = await axios.get(`${BASE_URL}/api/quote/${quoteNumber}`);
                    if (response.data.success) {
                        loadQuote(response.data.quote);
                        // Clear the URL parameter after loading
                        window.history.replaceState({}, document.title, window.location.pathname);
                    }
                } catch (error) {
                    console.error('Error loading quote from URL:', error);
                }
            }
        };

        loadQuoteFromUrl();
    }, [loggedIn]);

    // Add logout function
    const handleLogout = async () => {
        try {
            await axios.post(`${BASE_URL}/api/logout`);
            setUser(null);
            setLoggedIn(false);
        } catch (error) {
            console.error('Logout failed:', error);
        }
    };



    const fetchQuotes = async () => {
        try {
            const response = await axios.get(`${BASE_URL}/api/quotes/${user.id}`);
            if (response.data.success) {
                // Add logging to inspect the quotes data
                console.log('Raw quotes data:', response.data.quotes);
                
                const validQuotes = response.data.quotes.filter(quote => {
                    try {
                        // Check if quote_details is already an object
                        if (typeof quote.quote_details === 'object') {
                            return true;
                        }
                        // Try parsing if it's a string
                        JSON.parse(quote.quote_details);
                        return true;
                    } catch (error) {
                        console.error('Invalid quote details for quote number:', quote.quote_number, error);
                        return false;
                    }
                });
                
                setQuotes(validQuotes);
                setShowQuotesModal(true);
            }
        } catch (err) {
            console.error('Error fetching quotes:', err);
        }
    };

    const saveQuote = async (recommendation, answers, slotExceeded) => {
        try {
            const quoteNumber = new Date().getTime().toString();
            const priceInfo = getTotalPrice(recommendation);
            const createdAt = new Date().toISOString(); // Add creation date
            
            // Set the creation date in state immediately
            setQuoteCreatedAt(createdAt);
            console.log("Setting quote created at:", createdAt);
            
            const quoteDetails = {
                recommendation,
                answers,
                facilityInfo: {
                    email: userInfo.email,
                    name: userInfo.name,
                    company: userInfo.company,
                    phoneNumber: userInfo.phoneNumber,
                    projectName: userInfo.projectName,
                    city: userInfo.city,
                    state: userInfo.state
                },
                items: priceInfo.items,
                totalPrice: priceInfo.totalPrice,
                createdAt: createdAt, // Include creation date in quote details
                slotExceeded: slotExceeded
            };

            // Ensure proper JSON stringification
            const stringifiedDetails = JSON.stringify(quoteDetails);

            const response = await axios.post(`${BASE_URL}/api/quotes`, {
                userId: user.id,
                quoteNumber: quoteNumber,
                quoteDetails: stringifiedDetails
            });
            
            if (response.data.success) {
                setCurrentQuoteNumber(quoteNumber);
            }
        } catch (err) {
            console.error('Error saving quote:', err);
        }
    };
    

    const loadQuote = (quote) => {
        try {
            // Handle quotes from admin view which have a different structure
            const details = quote.details || JSON.parse(quote.quote_details);
            
            // Set created_at from the quote object
            setQuoteCreatedAt(quote.created_at);
            
            setAnswers(details.answers || {});
            let recommendationStr = '';
            if (details.recommendation) {
                const [modelLine, accessoriesLine] = details.recommendation.split(' with ');
                const model = modelLine.replace('Proteus ', '').trim();
                recommendationStr = `Proteus ${model}`;
                if (accessoriesLine) {
                    recommendationStr += ` with ${accessoriesLine}`;
                }
            }

            setAnswers(details.answers || {});
            setRecommendation(recommendationStr);
            setUserInfo(details.facilityInfo || {});
            setCurrentQuoteNumber(quote.quote_number);
            
            // Recalculate all accessories and prices
            const tankCount = parseInt(details.answers['How many Tanks/Probes, for level monitoring?']) || 1;
            
            // Force a recalculation of the recommendation with all accessories
            const baseRecommendation = Questions.determineInitialRecommendation(details.answers);
            const finalRecommendationResult = Questions.determineFinalRecommendation(baseRecommendation, details.answers);
            
            // Handle both old and new return formats
            let finalAccessories = [];
            let slotExceeded = false;
            
            if (finalRecommendationResult) {
                if (Array.isArray(finalRecommendationResult)) {
                    // Old format - just an array of accessories
                    finalAccessories = finalRecommendationResult;
                } else if (typeof finalRecommendationResult === 'object') {
                    // New format - object with accessories and slotExceeded
                    finalAccessories = finalRecommendationResult.accessories || [];
                    slotExceeded = finalRecommendationResult.slotExceeded || false;
                }
            }
            
            const fullRecommendation = `Proteus ${baseRecommendation}${finalAccessories.length > 0 ? ' with ' + finalAccessories.join(', ') : ''}`;
            
            setRecommendation(fullRecommendation);
            
            // Now calculate prices after setting full recommendation
            const priceInfo = getTotalPrice();
            setItems(priceInfo.items || []);
            setTotalPrice(priceInfo.totalPrice || 0);
            
            setShowQuotesModal(false);
            setShowModal(false);
            setInitialStep(false);
        } catch (error) {
            console.error('Error loading quote:', error);
        }
    };

    // Function to check if the current question has been answered
    const isCurrentQuestionAnswered = () => {
        const currentQuestion = initialStep 
            ? Questions.initialQuestions[currentQuestionIndex].question 
            : filteredSections[currentSectionIndex]?.questions[currentQuestionIndex];
        
        if (!currentQuestion) return false;

        // Check if it's a multi-tank question
        const multiTankQuestions = [
            'AST OR UST?',
            'Tank Material Type?',
            'What is the Product / Fuel Type in each Tank?',
            'Opening for Probe?',
            'Inside diameter of tanks?',
            'Probe Length',
            '2-conductor field wiring for interstice sensors?'
        ];

        if (multiTankQuestions.includes(currentQuestion)) {
            // For multi-tank questions, check if all tanks have been answered
            const tankCount = parseInt(answers['How many Tanks/Probes, for level monitoring?']) || 1;
            return Array.from({ length: tankCount }).every((_, index) => 
                answers[`${currentQuestion} - Tank ${index + 1}`] && 
                answers[`${currentQuestion} - Tank ${index + 1}`] !== ''
            );
        }

        // Check if it's the Remote Overfill Alarm question
        if (currentQuestion === 'Remote Overfill Alarm?') {
            const fillLocations = parseInt(answers['How many Fill locations for remote overflow alarms?']) || 0;
            if (fillLocations === 0 || answers['How many Fill locations for remote overflow alarms?'] === 'None') {
                return true; // Skip validation if no fill locations
            }
            return Array.from({ length: fillLocations }).every((_, index) => 
                answers[`${currentQuestion} - Location ${index + 1}`] && 
                answers[`${currentQuestion} - Location ${index + 1}`] !== ''
            );
        }

        // For regular questions, just check if the answer exists and is not empty
        return answers[currentQuestion] && answers[currentQuestion] !== '';
    };

    const handleKeyUp = (e) => 
    {
        if (e.key === 'Enter' && isCurrentQuestionAnswered()) 
        {
            handleNextQuestion();
        }
    };

    const [tankCount, setTankCount] = useState(1); // New state for tank count

    const handleInputChange = (event, questionKey = null) => 
    {
        const value = event.target.value;

        const questionText = questionKey || (initialStep
            ? Questions.initialQuestions[currentQuestionIndex].question
            : filteredSections[currentSectionIndex].questions[currentQuestionIndex]);
    
        // If setting AST for tanks, automatically set the dependent answers
        if (questionText.startsWith('AST OR UST?')) {
            setAnswers(prev => {
                const newAnswers = { ...prev, [questionText]: value };
                // If all tanks are AST after this change, force Inventory only probe
                if (areAllTanksAST(answers)) {
                    newAnswers['Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?'] = 'Inventory only probe';
                }
                return newAnswers;
            });
        }
        // If selecting "5 to 8 Probes and 0 Sensors", clear all sensor-related answers
        else if (questionText === 'How many Probes and Sensors are required?' && value === '5 to 8 Probes and 0 Sensors') {
            setAnswers(prev => {
                const newAnswers = { ...prev, [questionText]: value };
                // Clear all sensor-related answers
                delete newAnswers['How many Total Leak Sensors?'];
                delete newAnswers['How many Sump / Dispenser Sensors?'];
                delete newAnswers['How many Interstice Sensors?'];
                delete newAnswers['Is the interstice wet(BRINE)?'];
                delete newAnswers['Is Product Distinguishing required for Interstice?'];
                delete newAnswers['Opening for Interstice Sensor?'];
                delete newAnswers['2-conductor field wiring for interstice sensors?'];
                return newAnswers;
            });
        }
        else 
        {
            setAnswers(prev => ({ ...prev, [questionText]: value }));
        }

        
        if (questionText === "How many Tanks/Probes, for level monitoring?") 
        {
            const tankCountValue = parseInt(value, 10) || 1;
            setTankCount(tankCountValue);
            setAnswers(prev => ({ ...prev, [questionText]: tankCountValue }));
            //console.log("Tank count updated to:", tankCountValue);
        }
        else if (questionText.includes(" - Tank")) {
            // Store tank-specific answers under a unique key for each tank
            setAnswers(prev => ({ ...prev, [questionText]: value }));
            //console.log("Tank-specific answer updated:", questionText, value);
        }
        else if (questionText === "How many Total Leak Sensors?") {
            const newTotalSensors = parseInt(value) || 0;
            setAnswers(prev => {
                const newAnswers = { ...prev, [questionText]: value };
                
                // Validate and clear sump/interstice values if they exceed the new total
                const currentSump = parseInt(newAnswers['How many Sump / Dispenser Sensors?']) || 0;
                const currentInterstice = parseInt(newAnswers['How many Interstice Sensors?']) || 0;
                
                // If the sum of current values exceeds the new total, clear them
                if (currentSump + currentInterstice > newTotalSensors) {
                    // Clear both values so user can re-enter them
                    newAnswers['How many Sump / Dispenser Sensors?'] = '';
                    newAnswers['How many Interstice Sensors?'] = '';
                }
                
                return newAnswers;
            });
        }
        else if (questionText === 'How many Sump / Dispenser Sensors?') {
            setAnswers(prev => {
                const newAnswers = { ...prev, [questionText]: value };
                const totalSensors = parseInt(newAnswers['How many Total Leak Sensors?']) || 0;
                const newSumpValue = parseInt(value) || 0;
                
                // If user previously allocated all sensors to sump (interstice was auto-set to 0)
                // and now they're changing sump value, clear the interstice answer
                if (newAnswers['How many Interstice Sensors?'] === '0' && newSumpValue < totalSensors) {
                    newAnswers['How many Interstice Sensors?'] = '';
                }
                // If user is now allocating all sensors to sump, auto-set interstice to 0
                else if (newSumpValue === totalSensors && totalSensors > 0) {
                    newAnswers['How many Interstice Sensors?'] = '0';
                }
                
                return newAnswers;
            });
        }
        else if (questionText === 'How many Interstice Sensors?') {
            // Options are now calculated dynamically, no need to update limits
            setAnswers(prev => ({ ...prev, [questionText]: value }));
        }
        else if (questionText === 'Would you like VLD or CITLD CODE-P?')
        {
            setAnswers(prev => {
                const newAnswers = { ...prev, [questionText]: value };
                // Auto-set probe to Leak Detection if VLD or CITLD is selected
                if (value === 'VLD' || value === 'CITLD Code-P') {
                    newAnswers['Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?'] = 'Leak Detection probe';
                } 
                // Clear probe answer if "No" is selected (allow them to choose)
                else if (value === 'No' && newAnswers['Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?'] === 'Leak Detection probe') {
                    newAnswers['Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?'] = '';
                }
                return newAnswers;
            });
        }
        else if (questionText === 'Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?')
        {
            setAnswers(prev => {
                const newAnswers = { ...prev, [questionText]: value, 'Probe Length': '' };
                Questions.updateProbeLengthOptions(value, newAnswers);
                return newAnswers;
            });
        }
        else if (questionText === 'Is Product Distinguishing required for Interstice?' && value === 'Yes') 
        {
            // Auto-answer the 2-conductor question as "No"
            setAnswers(prev => ({
                ...prev,
                [questionText]: value,
                '2-conductor field wiring for interstice sensors?': 'No'
            }));
        }
        else if (questionText === 'Is Product Distinguishing required for Sump/Dispenser Sensors?' && value === 'Yes') 
        {
            // Auto-answer the 2-conductor question as "No" for sump sensors
            setAnswers(prev => ({
                ...prev,
                [questionText]: value,
                '2-conductor field wiring for sump/dispenser sensors?': 'No'
            }));
        } 
        else 
        {
            setAnswers(prev => ({
                ...prev,
                [questionText]: value
            }));
        }
    };
    
     // Add this function to check if all tanks are AST
     const areAllTanksAST = (answers) => {
        const tankCount = parseInt(answers['How many Tanks/Probes, for level monitoring?']) || 0;
        if (tankCount <= 0) return false;
        return Array.from({ length: tankCount }).every((_, index) => 
            answers[`AST OR UST? - Tank ${index + 1}`] === 'AST'
        );
    };

    // Add this function to check if all tanks are steel
    const areAllTanksSteel = (answers) => {
        const tankCount = parseInt(answers['How many Tanks/Probes, for level monitoring?']) || 0;
        if (tankCount <= 0) return false;
        return Array.from({ length: tankCount }).every((_, index) => 
            answers[`Tank Material Type? - Tank ${index + 1}`] === 'Steel'
        );
    };

    // Helper function to check if any RAS-3 or lower devices are selected (requires EC-6 cable)
    const hasRAS3OrLowerDevices = (answers) => {
        const ras3OrLowerDevices = ['RAS-1-NYS', 'RAS-1', 'RAS-2', 'RAS-3'];
        const fillLocations = parseInt(answers['How many Fill locations for remote overflow alarms?']) || 0;
        if (fillLocations === 0 || answers['How many Fill locations for remote overflow alarms?'] === 'None') {
            return false;
        }
        for (let i = 0; i < fillLocations; i++) {
            const device = answers[`Remote Overfill Alarm? - Location ${i + 1}`];
            if (device && ras3OrLowerDevices.includes(device)) {
                return true;
            }
        }
        return false;
    };

    // Helper function to check if any RAS-4 devices are selected (requires EC-12 cable)
    const hasRAS4Devices = (answers) => {
        const fillLocations = parseInt(answers['How many Fill locations for remote overflow alarms?']) || 0;
        if (fillLocations === 0 || answers['How many Fill locations for remote overflow alarms?'] === 'None') {
            return false;
        }
        for (let i = 0; i < fillLocations; i++) {
            const device = answers[`Remote Overfill Alarm? - Location ${i + 1}`];
            if (device === 'RAS-4') {
                return true;
            }
        }
        return false;
    };

    // Helper function to check if any DDL devices are selected (requires EC-4 cable)
    const hasDDLDevices = (answers) => {
        const ddlDevices = ['DDL-1', 'DDL-2', 'DDL-3', 'DDL-4'];
        const fillLocations = parseInt(answers['How many Fill locations for remote overflow alarms?']) || 0;
        if (fillLocations === 0 || answers['How many Fill locations for remote overflow alarms?'] === 'None') {
            return false;
        }
        for (let i = 0; i < fillLocations; i++) {
            const device = answers[`Remote Overfill Alarm? - Location ${i + 1}`];
            if (device && ddlDevices.includes(device)) {
                return true;
            }
        }
        return false;
    };

        const handleNextQuestion = () => {
            // Check for hard stop on current initial question
            if (initialStep) {
                const currentInitialQuestion = Questions.initialQuestions[currentQuestionIndex];
                const currentAnswer = answers[currentInitialQuestion.question];
                
                // Check if this question has a hard stop condition
                if (currentInitialQuestion.hardStop && currentAnswer) {
                    const isHardStop = typeof currentInitialQuestion.hardStop === 'function'
                        ? currentInitialQuestion.hardStop(currentAnswer, answers)
                        : false;
                    
                    if (isHardStop) {
                        setHardStopMessage(currentInitialQuestion.hardStopMessage);
                        return; // Don't proceed to next question
                    }
                }
                
                // Clear any existing hard stop message
                setHardStopMessage(null);
                
                // If we're on the last initial question
                if (currentQuestionIndex === Questions.initialQuestions.length - 1) {
                    // Only determine initial recommendation if we don't already have one
                    if (!initialRecommendation) {
                        const recommendation = Questions.determineInitialRecommendation(answers);
                        setInitialRecommendation(recommendation);
                        setCurrentModel(recommendation);
                    }
                    
                    // Move to regular questions
                    setInitialStep(false);
                    setCurrentQuestionIndex(0);
                    setCurrentSectionIndex(0);
                } else {
                    // Move to next initial question
                    setCurrentQuestionIndex(currentQuestionIndex + 1);
                }
            }
            // Get all questions in order
            const allQuestions = [...Questions.initialQuestions, ...Questions.questions];
            
            // Find current question index in the full list
            const currentQuestion = initialStep 
                ? Questions.initialQuestions[currentQuestionIndex].question 
                : filteredSections[currentSectionIndex].questions[currentQuestionIndex];
            
            const currentFullIndex = allQuestions.findIndex(q => q.question === currentQuestion);
            
            // Move to next question
            if (currentFullIndex < allQuestions.length - 1) {
                let nextQuestionIndex = currentFullIndex + 1;
                let nextQuestion;
                let allRemaining = true;  // Flag to check if all remaining questions should be skipped
                
                // Keep looking for the next valid question
                do {
                    nextQuestion = allQuestions[nextQuestionIndex];
                    
                    // Check if this question should be skipped
                    const shouldSkip = (
                        // Skip if all tanks are AST and question is about leak detection
                        (areAllTanksAST(answers) && 
                            (nextQuestion.question === 'Would you like VLD or CITLD CODE-P?' ||
                             nextQuestion.question === 'Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?')) ||
                        // Skip probe selection if VLD or CITLD is selected (auto-select Leak Detection probe)
                        ((answers['Would you like VLD or CITLD CODE-P?'] === 'VLD' || 
                          answers['Would you like VLD or CITLD CODE-P?'] === 'CITLD Code-P') &&
                         nextQuestion.question === 'Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?') ||
                        // Skip if product distinguishing is required and question is about 2-conductor wiring for interstice
                        (answers['Is Product Distinguishing required for Interstice?'] === 'Yes' &&
                         nextQuestion.question === '2-conductor field wiring for interstice sensors?') ||
                        // Skip if product distinguishing is required and question is about 2-conductor wiring for sump/dispenser
                        (answers['Is Product Distinguishing required for Sump/Dispenser Sensors?'] === 'Yes' &&
                         nextQuestion.question === '2-conductor field wiring for sump/dispenser sensors?') ||
                        // Skip sump product distinguishing question if no sump sensors
                        (answers['How many Sump / Dispenser Sensors?'] === '0' &&
                         nextQuestion.question === 'Is Product Distinguishing required for Sump/Dispenser Sensors?') ||
                        // Skip sump 2-conductor wiring question if no sump sensors
                        (answers['How many Sump / Dispenser Sensors?'] === '0' &&
                         nextQuestion.question === '2-conductor field wiring for sump/dispenser sensors?') ||
                        // Skip brine question if all tanks are steel
                        (areAllTanksSteel(answers) && 
                         nextQuestion.question === 'Is the interstice wet(BRINE)?') ||
                        // Skip additional relays question if no extra relays needed (None selected)
                        (answers['Proteus contains 5 relay outputs standard. How many ADDITIONAL relays do you need?'] === 'None' &&
                         nextQuestion.question === 'How many additional relays are needed?') ||
                        // Skip BACnet device question if BACnet is not selected
                        (nextQuestion.question === 'Please select a BACnet device that supports your protocol requirements.' &&
                         answers['Are additional Communication Protocols such as Modbus or BACnet required?'] !== 'BACnet') ||
                        // Skip Remote Overfill Alarm question if None is selected for fill locations
                        (answers['How many Fill locations for remote overflow alarms?'] === 'None' &&
                         nextQuestion.question === 'Remote Overfill Alarm?') ||
                        // Skip EC-6 RAS Alarm cable question if no RAS-3 or lower devices selected
                        (nextQuestion.question === "RAS Alarm cable (EC-6) for RAS-1 to RAS-3. How Many Spools?" &&
                         !hasRAS3OrLowerDevices(answers)) ||
                        // Skip EC-12 RAS Alarm cable question if no RAS-4 devices selected
                        (nextQuestion.question === "RAS Alarm cable (EC-12) for RAS-4. How Many Spools?" &&
                         !hasRAS4Devices(answers)) ||
                        // Skip EC-4 DDL Alarm cable question if no DDL devices selected
                        (nextQuestion.question === "DDL Alarm cable (EC-4). How Many Spools?" &&
                         !hasDDLDevices(answers)) ||
                        // Skip all sensor questions if 5-8 probes and 0 sensors selected
                        (answers['How many Probes and Sensors are required?'] === '5 to 8 Probes and 0 Sensors' &&
                         nextQuestion.section === 'LEAK SENSORS QUESTIONS') ||
                         // Skip all sensor questions (except the total question) if 0 is selected for total leak sensors
                         (answers['How many Total Leak Sensors?'] === '0' &&
                         nextQuestion.section === 'LEAK SENSORS QUESTIONS' &&
                         nextQuestion.question !== 'How many Total Leak Sensors?') ||
                        // Skip interstice sensors question if all sensors are allocated to sump
                        (nextQuestion.question === 'How many Interstice Sensors?' &&
                         answers['How many Total Leak Sensors?'] &&
                         answers['How many Sump / Dispenser Sensors?'] &&
                         parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?'])) ||
                        // Skip brine question if all sensors are allocated to sump (no interstice sensors)
                        (nextQuestion.question === 'Is the interstice wet(BRINE)?' &&
                         answers['How many Total Leak Sensors?'] &&
                         answers['How many Sump / Dispenser Sensors?'] &&
                         parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?'])) ||
                        // Skip product distinguishing for interstice if all sensors are allocated to sump
                        (nextQuestion.question === 'Is Product Distinguishing required for Interstice?' &&
                         answers['How many Total Leak Sensors?'] &&
                         answers['How many Sump / Dispenser Sensors?'] &&
                         parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?'])) ||
                        // Skip interstice sensor opening if all sensors are allocated to sump
                        (nextQuestion.question === 'Opening for Interstice Sensor?' &&
                         answers['How many Total Leak Sensors?'] &&
                         answers['How many Sump / Dispenser Sensors?'] &&
                         parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?'])) ||
                        // Skip interstice wiring question if all sensors are allocated to sump
                        (nextQuestion.question === '2-conductor field wiring for interstice sensors?' &&
                         answers['How many Total Leak Sensors?'] &&
                         answers['How many Sump / Dispenser Sensors?'] &&
                         parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?']))
                    );
                    
                    // Auto-answer probe question when VLD or CITLD is selected
                    if ((answers['Would you like VLD or CITLD CODE-P?'] === 'VLD' || 
                         answers['Would you like VLD or CITLD CODE-P?'] === 'CITLD Code-P') &&
                        nextQuestion.question === 'Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?') {
                        setAnswers(prev => ({ 
                            ...prev, 
                            'Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?': 'Leak Detection probe' 
                        }));
                    }
                    
                    // Auto-answer interstice sensors to 0 if all sensors are allocated to sump
                    if (nextQuestion.question === 'How many Interstice Sensors?' &&
                        answers['How many Total Leak Sensors?'] &&
                        answers['How many Sump / Dispenser Sensors?'] &&
                        parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?'])) {
                        setAnswers(prev => ({ 
                            ...prev, 
                            'How many Interstice Sensors?': '0' 
                        }));
                    }
                    
                    if (!shouldSkip) {
                        allRemaining = false;
                        break;
                    }
                    
                    nextQuestionIndex++;
                } while (nextQuestionIndex < allQuestions.length);
                
                // If all remaining questions should be skipped or we're at the end, generate quote
                if (allRemaining || nextQuestionIndex >= allQuestions.length) {
                    const finalRecommendationResult = Questions.determineFinalRecommendation(initialRecommendation, answers);
                    
                    // Handle both old and new return formats
                    let finalAccessories = [];
                    let slotExceeded = false;
                    
                    if (finalRecommendationResult) {
                        if (Array.isArray(finalRecommendationResult)) {
                            // Old format - just an array of accessories
                            finalAccessories = finalRecommendationResult;
                        } else if (typeof finalRecommendationResult === 'object') {
                            // New format - object with accessories and slotExceeded
                            finalAccessories = finalRecommendationResult.accessories || [];
                            slotExceeded = finalRecommendationResult.slotExceeded || false;
                        }
                    }
                    
                    const finalRecommendation = `Proteus ${initialRecommendation}${finalAccessories.length > 0 ? ' with ' + finalAccessories.join(', ') : ''}`;
                    setRecommendation(finalRecommendation);
                    if (finalRecommendation) {
                        // Set the creation date for the new quote
                        const newCreatedAt = new Date().toISOString();
                        setQuoteCreatedAt(newCreatedAt);
                        console.log("Setting new quote created at:", newCreatedAt);
                        saveQuote(finalRecommendation, answers, slotExceeded);
                    }
                } else if (nextQuestionIndex < allQuestions.length) {
                    // Handle next question logic
                    const isInInitial = Questions.initialQuestions.some(q => q.question === nextQuestion.question);
                    
                    if (isInInitial) {
                        setInitialStep(true);
                        setCurrentQuestionIndex(Questions.initialQuestions.findIndex(q => q.question === nextQuestion.question));
                    } else {
                        setInitialStep(false);
                        const sectionIndex = filteredSections.findIndex(section => 
                            section.questions.includes(nextQuestion.question)
                        );
                        const questionIndex = filteredSections[sectionIndex].questions.indexOf(nextQuestion.question);
                        
                        setCurrentSectionIndex(sectionIndex);
                        setCurrentQuestionIndex(questionIndex);
                    }
                }
            } else {
                // We're at the last question, generate quote
                const finalRecommendationResult = Questions.determineFinalRecommendation(initialRecommendation, answers);
                
                // Handle both old and new return formats
                let finalAccessories = [];
                let slotExceeded = false;
                
                if (finalRecommendationResult) {
                    if (Array.isArray(finalRecommendationResult)) {
                        // Old format - just an array of accessories
                        finalAccessories = finalRecommendationResult;
                    } else if (typeof finalRecommendationResult === 'object') {
                        // New format - object with accessories and slotExceeded
                        finalAccessories = finalRecommendationResult.accessories || [];
                        slotExceeded = finalRecommendationResult.slotExceeded || false;
                    }
                }
                
                const finalRecommendation = `Proteus ${initialRecommendation}${finalAccessories.length > 0 ? ' with ' + finalAccessories.join(', ') : ''}`;
                setRecommendation(finalRecommendation);
                if (finalRecommendation) {
                    // Set the creation date for the new quote
                    const newCreatedAt = new Date().toISOString();
                    setQuoteCreatedAt(newCreatedAt);
                    console.log("Setting new quote created at:", newCreatedAt);
                    saveQuote(finalRecommendation, answers, slotExceeded);
                }
            }
        };
            
            

    const resetQuoteGenerator = () => {
        setAnswers({});
        setInitialRecommendation(null);
        setRecommendation(null);
        setCurrentSectionIndex(0);
        setCurrentQuestionIndex(0);
        setUserInfo({
            email: '',
            name: '',
            company: '',
            phoneNumber: ''
        });
        setShowModal(true);
        setInitialStep(true);
        setCurrentQuoteNumber(null);
        setQuoteCreatedAt(null); // Reset the quote creation date
        setHardStopMessage(null); // Reset hard stop message
    };

    const handlePreviousQuestion = () => {
        // Get all questions in order
        const allQuestions = [...Questions.initialQuestions, ...Questions.questions];
        
        // Find current question index in the full list
        const currentQuestion = initialStep 
            ? Questions.initialQuestions[currentQuestionIndex].question 
            : filteredSections[currentSectionIndex].questions[currentQuestionIndex];
        
        const currentFullIndex = allQuestions.findIndex(q => q.question === currentQuestion);
        
        // Move to previous question
        if (currentFullIndex > 0) {
            let prevQuestionIndex = currentFullIndex - 1;
            let prevQuestion;
            
            // Keep looking for the previous valid question
            do {
                prevQuestion = allQuestions[prevQuestionIndex];
                
                // Check if this question should be skipped
                const shouldSkip = (
                    // Skip if all tanks are AST and question is about leak detection
                    (areAllTanksAST(answers) && 
                        (prevQuestion.question === 'Would you like VLD or CITLD CODE-P?' ||
                         prevQuestion.question === 'Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?')) ||
                    // Skip probe selection if VLD or CITLD is selected (auto-select Leak Detection probe)
                    ((answers['Would you like VLD or CITLD CODE-P?'] === 'VLD' || 
                      answers['Would you like VLD or CITLD CODE-P?'] === 'CITLD Code-P') &&
                     prevQuestion.question === 'Leak Detection probe (i.e. 0.2gph/ 0.1gph Leak Detection probe), or Inventory only probe?') ||
                    // Skip if product distinguishing is required and question is about 2-conductor wiring for interstice
                    (answers['Is Product Distinguishing required for Interstice?'] === 'Yes' &&
                     prevQuestion.question === '2-conductor field wiring for interstice sensors?') ||
                    // Skip if product distinguishing is required and question is about 2-conductor wiring for sump/dispenser
                    (answers['Is Product Distinguishing required for Sump/Dispenser Sensors?'] === 'Yes' &&
                     prevQuestion.question === '2-conductor field wiring for sump/dispenser sensors?') ||
                    // Skip sump product distinguishing question if no sump sensors
                    (answers['How many Sump / Dispenser Sensors?'] === '0' &&
                     prevQuestion.question === 'Is Product Distinguishing required for Sump/Dispenser Sensors?') ||
                    // Skip sump 2-conductor wiring question if no sump sensors
                    (answers['How many Sump / Dispenser Sensors?'] === '0' &&
                     prevQuestion.question === '2-conductor field wiring for sump/dispenser sensors?') ||
                    // Skip brine question if all tanks are steel
                    (areAllTanksSteel(answers) && 
                     prevQuestion.question === 'Is the interstice wet(BRINE)?') ||
                    // Skip additional relays question if no extra relays needed (None selected)
                    (answers['Proteus contains 5 relay outputs standard. How many ADDITIONAL relays do you need?'] === 'None' &&
                     prevQuestion.question === 'How many additional relays are needed?') ||
                    // Skip BACnet device question if BACnet is not selected
                    (prevQuestion.question === 'Please select a BACnet device that supports your protocol requirements.' &&
                     answers['Are additional Communication Protocols such as Modbus or BACnet required?'] !== 'BACnet') ||
                    // Skip Remote Overfill Alarm question if None is selected for fill locations
                    (answers['How many Fill locations for remote overflow alarms?'] === 'None' &&
                     prevQuestion.question === 'Remote Overfill Alarm?') ||
                    // Skip EC-6 RAS Alarm cable question if no RAS-3 or lower devices selected
                    (prevQuestion.question === "RAS Alarm cable (EC-6) for RAS-1 to RAS-3. How Many Spools?" &&
                     !hasRAS3OrLowerDevices(answers)) ||
                    // Skip EC-12 RAS Alarm cable question if no RAS-4 devices selected
                    (prevQuestion.question === "RAS Alarm cable (EC-12) for RAS-4. How Many Spools?" &&
                     !hasRAS4Devices(answers)) ||
                    // Skip EC-4 DDL Alarm cable question if no DDL devices selected
                    (prevQuestion.question === "DDL Alarm cable (EC-4). How Many Spools?" &&
                     !hasDDLDevices(answers)) ||
                    // Skip all sensor questions if 5-8 probes and 0 sensors selected
                    (answers['How many Probes and Sensors are required?'] === '5 to 8 Probes and 0 Sensors' &&
                     prevQuestion.section === 'LEAK SENSORS QUESTIONS') ||
                    // Skip all sensor questions if 0 is selected for total leak sensors (but not the total sensors question itself)
                    (answers['How many Total Leak Sensors?'] === '0' &&
                     prevQuestion.section === 'LEAK SENSORS QUESTIONS' &&
                     prevQuestion.question !== 'How many Total Leak Sensors?') ||
                    // Skip interstice sensors question if all sensors are allocated to sump
                    (prevQuestion.question === 'How many Interstice Sensors?' &&
                     answers['How many Total Leak Sensors?'] &&
                     answers['How many Sump / Dispenser Sensors?'] &&
                     parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?'])) ||
                    // Skip brine question if all sensors are allocated to sump (no interstice sensors)
                    (prevQuestion.question === 'Is the interstice wet(BRINE)?' &&
                     answers['How many Total Leak Sensors?'] &&
                     answers['How many Sump / Dispenser Sensors?'] &&
                     parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?'])) ||
                    // Skip product distinguishing for interstice if all sensors are allocated to sump
                    (prevQuestion.question === 'Is Product Distinguishing required for Interstice?' &&
                     answers['How many Total Leak Sensors?'] &&
                     answers['How many Sump / Dispenser Sensors?'] &&
                     parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?'])) ||
                    // Skip interstice sensor opening if all sensors are allocated to sump
                    (prevQuestion.question === 'Opening for Interstice Sensor?' &&
                     answers['How many Total Leak Sensors?'] &&
                     answers['How many Sump / Dispenser Sensors?'] &&
                     parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?'])) ||
                    // Skip interstice wiring question if all sensors are allocated to sump
                    (prevQuestion.question === '2-conductor field wiring for interstice sensors?' &&
                     answers['How many Total Leak Sensors?'] &&
                     answers['How many Sump / Dispenser Sensors?'] &&
                     parseInt(answers['How many Total Leak Sensors?']) === parseInt(answers['How many Sump / Dispenser Sensors?']))
                );
                
                if (!shouldSkip) break;
                
                prevQuestionIndex--;
            } while (prevQuestionIndex >= 0);
            
            // If we found a valid previous question
            if (prevQuestionIndex >= 0) {
                const isInInitial = Questions.initialQuestions.some(q => q.question === prevQuestion.question);
                
                if (isInInitial) {
                    setInitialStep(true);
                    setCurrentQuestionIndex(Questions.initialQuestions.findIndex(q => q.question === prevQuestion.question));
                } else {
                    setInitialStep(false);
                    const sectionIndex = filteredSections.findIndex(section => 
                        section.questions.includes(prevQuestion.question)
                    );
                    const questionIndex = filteredSections[sectionIndex].questions.indexOf(prevQuestion.question);
                    
                    setCurrentSectionIndex(sectionIndex);
                    setCurrentQuestionIndex(questionIndex);
                }
            }
        }
    };

    const [progress, setProgress] = useState(0);

    useEffect(() => {
        if (initialStep) {
            
            setProgress(((currentQuestionIndex) / Questions.initialQuestions.length) * 100);
        } else {
            // Get total number of questions across all sections
            const totalQuestions = Questions.questions.length;
            
            // Calculate the absolute index of the current question
            let completedQuestions = Questions.questions.findIndex(q => 
                q.question === filteredSections[currentSectionIndex].questions[currentQuestionIndex]
            );
            
            // If question is found, add 1 to include current question
            if (completedQuestions !== -1) {
                completedQuestions += 1;
            }
            
            // Calculate progress percentage based on total questions
            setProgress((completedQuestions / totalQuestions) * 100);
        }
    }, [currentQuestionIndex, currentSectionIndex, initialStep, filteredSections]);

    const currentQuestionObj = initialStep
        ? Questions.initialQuestions[currentQuestionIndex]
        : Questions.questions.find(q => q.question === filteredSections[currentSectionIndex]?.questions[currentQuestionIndex]);
    
        const getTotalPrice = (recommendationStr = recommendation, discount = userDiscount, currentAnswers = null) => {
            // Use passed answers or fall back to state (currentAnswers allows passing updated answers before state updates)
            const answersToUse = currentAnswers || answers;
            
            let totalPrice = 0;
            const generalItems = []; // For model and general accessories
            const tankItems = []; // For tank-specific accessories
            const processedAccessories = new Map();
            const accessoryCounts = new Map();

            if (!recommendationStr) {
                return { totalPrice: 0, items: [], discountedTotal: 0 };
            }

            const [modelLine, accessoriesLine] = recommendationStr.split(' with ');
            const model = modelLine.replace('Proteus ', '').trim();

            // Add model price
            if (pricingData.models && pricingData.models[model]) {
                const modelPrice = pricingData.models[model];
                totalPrice += modelPrice;
                generalItems.push({ 
                    name: `Proteus Model - ${model}`, 
                    quantity: 1, 
                    price: modelPrice 
                });
            }

            if (accessoriesLine) {
                const accessoriesList = accessoriesLine.split(',').map(item => item.trim());
                
                // First, count occurrences of each accessory
                accessoriesList.forEach(accessory => {
                    // Special handling for expansion boards (XB-800, XB-416)
                    if (accessory === 'XB-800' || accessory === 'XB-416') {
                        accessoryCounts.set(accessory, (accessoryCounts.get(accessory) || 0) + 1);
                    }
                });
                
                // Process all accessories
                accessoriesList.forEach(accessory => {
                    let accessoryName = accessory;
                    let quantity = 1;
                    let displayName = accessoryName;
                    
                    // Special handling for expansion boards
                    if ((accessoryName === 'XB-800' || accessoryName === 'XB-416') && accessoryCounts.has(accessoryName)) {
                        // If this is an expansion board with multiple occurrences
                        if (!processedAccessories.has(accessoryName)) {
                            const count = accessoryCounts.get(accessoryName);
                            const accessoryPrice = pricingData.parts[accessoryName] || 0;
                            const totalItemPrice = accessoryPrice * count;
                            
                            generalItems.push({
                                name: `${accessoryName}`,
                                quantity: count,
                                price: accessoryPrice,
                                totalPrice: totalItemPrice
                            });
                            
                            totalPrice += totalItemPrice;
                            processedAccessories.set(accessoryName, true);
                        }
                        return;
                    }
                    
                    // Skip if already processed (for non-expansion board accessories)
                    if (processedAccessories.has(accessoryName)) return;

                    // Handle tank-specific accessories
                    if (accessoryName.startsWith('Tank')) {
                        const [tankPart, actualPart] = accessoryName.split(': ');
                        // Check if the part exists in pricing data
                        if (actualPart && pricingData.parts[actualPart]) {
                            const accessoryPrice = pricingData.parts[actualPart];
                            const totalItemPrice = accessoryPrice * quantity;
                            
                            tankItems.push({ 
                                name: accessoryName, 
                                quantity: quantity, 
                                price: accessoryPrice,
                                totalPrice: totalItemPrice
                            });
                            totalPrice += totalItemPrice;
                            processedAccessories.set(accessoryName, true);
                        } else {
                            console.warn(`Price not found for part: ${actualPart}`);
                        }
                        return;
                    }

                    // Handle sump sensors - only process each type once
                    if ((accessoryName === 'BX-LS' || accessoryName === 'LSF' || accessoryName === 'BX-PDS') && 
                        !processedAccessories.has('SUMP_SENSORS_PROCESSED')) {
                        
                        // Mark all sump sensor types as processed to prevent duplicates
                        processedAccessories.set('SUMP_SENSORS_PROCESSED', true);
                        processedAccessories.set('BX-LS', true);
                        processedAccessories.set('LSF', true);
                        processedAccessories.set('BX-PDS', true);
                        processedAccessories.set('BX-UT', true); // Mark BX-UT as processed to prevent duplicates
                        
                        const sumpSensorCount = parseInt(answersToUse['How many Sump / Dispenser Sensors?']) || 0;
                        if (sumpSensorCount > 0) {
                            const uses2Conductor = answersToUse['2-conductor field wiring for sump/dispenser sensors?'] === 'Yes';
                            const isProductDistinguishing = answersToUse['Is Product Distinguishing required for Sump/Dispenser Sensors?'] === 'Yes';
                            
                            let sensorModel;
                            if (uses2Conductor) {
                                sensorModel = 'LSF';
                                quantity = sumpSensorCount;
                                // Also add BX-UT
                                const utPrice = pricingData.parts['BX-UT'] || 0;
                                generalItems.push({
                                    name: 'Sumps - BX-UT',
                                    quantity: sumpSensorCount,
                                    price: utPrice,
                                    totalPrice: utPrice * sumpSensorCount
                                });
                                totalPrice += utPrice * sumpSensorCount;
                            } else if (isProductDistinguishing) {
                                sensorModel = 'BX-PDS';
                                quantity = sumpSensorCount;
                            } else {
                                sensorModel = 'BX-LS';
                                quantity = sumpSensorCount;
                            }
                            
                            const sensorPrice = pricingData.parts[sensorModel] || 0;
                            const totalItemPrice = sensorPrice * quantity;
                            
                            generalItems.push({
                                name: `Sumps - ${sensorModel}`,
                                quantity: quantity,
                                price: sensorPrice,
                                totalPrice: totalItemPrice
                            });
                            
                            totalPrice += totalItemPrice;
                            return;
                        }
                    }

                    // Handle BX-UT for interstice sensors separately to prevent duplicates
                    if (accessoryName === 'BX-UT' && !processedAccessories.has('BX-UT')) {
                        // Check if this BX-UT is for interstice sensors
                        const intersticeSensorCount = parseInt(answersToUse['How many Interstice Sensors?']) || 0;
                        const uses2ConductorInterstice = answersToUse['2-conductor field wiring for interstice sensors?'] === 'Yes';
                        
                        if (intersticeSensorCount > 0 && uses2ConductorInterstice) {
                            const utPrice = pricingData.parts['BX-UT'] || 0;
                            generalItems.push({
                                name: 'BX-UT',
                                quantity: 1, // Only add one BX-UT for interstice sensors
                                price: utPrice,
                                totalPrice: utPrice
                            });
                            totalPrice += utPrice;
                            processedAccessories.set('BX-UT', true);
                            return;
                        }
                    }

                    // Handle special cases for cables
                    if (accessoryName === 'EC-6') {
                        quantity = parseInt(answersToUse["RAS Alarm cable (EC-6) for RAS-1 to RAS-3. How Many Spools?"], 10) || 0;
                        displayName = '(1000ft Spool) RAS Cable - EC-6';
                        accessoryName = 'EC-6';
                    } else if (accessoryName === 'EC-12') {
                        quantity = parseInt(answersToUse["RAS Alarm cable (EC-12) for RAS-4. How Many Spools?"], 10) || 0;
                        displayName = '(1000ft Spool) RAS Cable - EC-12';
                        accessoryName = 'EC-12';
                    } else if (accessoryName === 'DDL-EC-4') {
                        quantity = parseInt(answersToUse["DDL Alarm cable (EC-4). How Many Spools?"], 10) || 0;
                        displayName = '(1000ft Spool) DDL Cable - EC-4';
                        accessoryName = 'EC-4';
                    } else if (accessoryName.includes("EC-2")) {
                        quantity = parseInt(answersToUse["Probe cable(EC-2 500'/SPOOLS). How Many Spools?"], 10) || 0;
                        displayName = '(500ft Spool) Probe Cable - EC-2';
                        accessoryName = 'EC-2';
                    } else if (accessoryName.includes("EC-4")) {
                        quantity = parseInt(answersToUse["Sensors Cable(EC-4 1000'/SPOOLS)?"], 10) || 0;
                        displayName = 'Sensor Cable - EC-4';
                        accessoryName = 'EC-4';
                    } else if (accessoryName.includes("Remote Overfill Alarm")) {
                        // Extract the actual alarm type from the accessory name
                        const alarmType = accessoryName.split(': ')[1];
                        if (alarmType && pricingData.parts[alarmType]) {
                            const alarmPrice = pricingData.parts[alarmType];
                            
                            // Check if this is a location-specific alarm
                            const locationMatch = accessoryName.match(/Location (\d+)/);
                            if (locationMatch) {
                                const locationNumber = locationMatch[1];
                                generalItems.push({
                                    name: `Overfill Alarm - Location ${locationNumber}: ${alarmType}`,
                                    quantity: 1,
                                    price: alarmPrice,
                                    totalPrice: alarmPrice
                                });
                            } else {
                                generalItems.push({
                                    name: `Overfill Alarm: ${alarmType}`,
                                    quantity: 1,
                                    price: alarmPrice,
                                    totalPrice: alarmPrice
                                });
                            }
                            
                            totalPrice += alarmPrice;
                            // Don't mark as processed to allow multiple alarms
                        }
                        return;
                    } else if (accessoryName.includes("MODBUS") || accessoryName.includes("BACnet") || accessoryName.includes("C232-BAC") || accessoryName.includes("C485-BAC") || accessoryName.includes("CTCP")) {
                        const protocol = answersToUse["Are additional Communication Protocols such as Modbus or BACnet required?"];
                        if (protocol === 'BACnet') {
                            // Use the specific BACnet device from the detailed question
                            const bacnetDevice = answersToUse['Please select a BACnet device that supports your protocol requirements.'];
                            const bacnetDeviceMap = {
                                'Modbus RTU over RS232 to BACnet IP': 'C232-BAC',
                                'Modbus RTU over RS485 to BACnet IP': 'C485-BAC',
                                'Modbus TCP to BACnet IP': 'CTCP/IP-BAC',
                                'Modbus RTU over RS232 to BACnet MS/TP': 'C232-BAC-MSTP',
                                'Modbus RTU over RS485 to BACnet MS/TP': 'C485-BAC-MSTP',
                                'Modbus TCP to BACnet MS/TP': 'CTCP/IP-BAC-MSTP',
                                'BACnet Controller - TBD': 'BACnet Controller TBD'
                            };
                            const partNumber = bacnetDeviceMap[bacnetDevice] || 'BACnet module';
                            accessoryName = partNumber;
                            // Display part number with description
                            displayName = partNumber;
                        } else {
                            const protocolMap = {
                                'MODBUS TCP module': 'MODBUS TCP module',
                                'MODBUS Serial module': 'MODBUS Serial module'
                            };
                            accessoryName = protocolMap[protocol] || protocol;
                            displayName = accessoryName;
                        }
                    }

                    // Add the accessory with its quantity
                    if (pricingData.parts[accessoryName]) {
                        const accessoryPrice = pricingData.parts[accessoryName];
                        generalItems.push({
                            name: displayName,
                            quantity: quantity,
                            price: accessoryPrice,
                            totalPrice: accessoryPrice * quantity
                        });
                        totalPrice += accessoryPrice * quantity;
                        processedAccessories.set(accessoryName, true);
                    }

                    // Inside the accessoriesList.forEach loop, add this condition:
                    if (accessoryName === 'SSFK2-SF' || accessoryName === 'SSFK4-SF' || accessoryName === 'SL-34') {
                        const price = pricingData.parts[accessoryName] || 0;
                        generalItems.push({
                            name: accessoryName,
                            quantity: 1,
                            price: price,
                            totalPrice: price
                        });
                        totalPrice += price;
                        processedAccessories.set(accessoryName, true);
                        return;
                    }
                    
                    // Special handling for "Contact Omntec" message
                    if (accessoryName === 'Contact Omntec for additional slot solutions') {
                        // Remove this item from the accessories list - we'll handle it separately
                        // in the Recommendation component as a disclaimer
                        processedAccessories.set(accessoryName, true);
                        return;
                    }
                });
            }

            // Combine the items arrays - general items first, then tank items
            const items = [...generalItems, ...tankItems];

            // Calculate discounted total
            const discountedTotal = totalPrice - (totalPrice * (discount / 100));

            return { totalPrice, items, discountedTotal };
        };
          
          

    // Add this function to handle clicks outside the dropdown
    const handleClickOutside = (event) => {
        const dropdown = document.querySelector('.dropdown-content');
        const trigger = document.querySelector('.user-info-dropdown');
        
        if (dropdown && !dropdown.contains(event.target) && !trigger.contains(event.target)) {
            setIsDropdownOpen(false);
        }
    };

    // Add this useEffect to handle the click outside listener
    useEffect(() => {
        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    // re-fetch the quotes when the user clicks on the quotes button
    const handleBackToQuotesList = async () => {
        await fetchQuotes();  // Fetch latest quotes
        setShowQuotesModal(true);
    };

    const handleShowAdmin = () => {
        // Close all other modals first
        setShowQuotesModal(false);
        setShowModal(false);
        setAiModalOpen(false);
        // Then open admin modal
        setShowAdminModal(true);
    };

    // Add a useEffect to log quoteCreatedAt changes
    useEffect(() => {
        console.log("quoteCreatedAt changed:", quoteCreatedAt);
    }, [quoteCreatedAt]);

    if (!loggedIn) 
    {
        return showRegisterModal ? (
            <RegisterModal onClose={() => setShowRegisterModal(false)} />
        ) : (
            <LoginModal onLoginSuccess={(user) => { 
                setLoggedIn(true); 
                setUser(user);
                // Set user discount from login response
                if (user.discount !== undefined) {
                    setUserDiscount(parseFloat(user.discount) || 0);
                }
            }} openRegisterModal={() => setShowRegisterModal(true)} />
        );
    }

    if (showQuotesModal) {
        return (
            <>
                <NavBar 
                    handleLogout={handleLogout}
                    onShowAIAssistant={() => setAiModalOpen(true)}
                    onShowDashboard={handleBackToQuotesList}
                    onShowAdmin={handleShowAdmin}
                    user={user}
                    quotes={quotes}
                    dropdownSearchTerm={dropdownSearchTerm}
                    setDropdownSearchTerm={setDropdownSearchTerm}
                    isDropdownOpen={isDropdownOpen}
                    setIsDropdownOpen={setIsDropdownOpen}
                    loadQuote={loadQuote}
                    showAdminModal={showAdminModal}
                />
                <PreviousQuotesModal
                    quotes={quotes}
                    user={user}
                    userDiscount={userDiscount}
                    onNewQuote={() => {
                        setShowQuotesModal(false);
                        setShowModal(true);
                        resetQuoteGenerator();
                    }}
                    onSelectQuote={(quote) => { loadQuote(quote); }}
                />
            </>
        );
    }

    if (showModal) {
        return (
            <>
                <NavBar 
                    handleLogout={handleLogout}
                    onShowAIAssistant={() => setAiModalOpen(true)}
                    onShowDashboard={handleBackToQuotesList}
                    onShowAdmin={handleShowAdmin}
                    user={user}
                    quotes={quotes}
                    dropdownSearchTerm={dropdownSearchTerm}
                    setDropdownSearchTerm={setDropdownSearchTerm}
                    isDropdownOpen={isDropdownOpen}
                    setIsDropdownOpen={setIsDropdownOpen}
                    loadQuote={loadQuote}
                    showAdminModal={showAdminModal}
                />
                <UserInfoModal onSave={() => setShowModal(false)} userInfo={userInfo} setUserInfo={setUserInfo} />
            </>
        );
    }

    if (showAdminModal) {
        return <AdminModal 
            onClose={() => {
                setShowAdminModal(false);
                setShowQuotesModal(true); // Go back to dashboard
            }} 
            onSelectQuote={(quote) => {
                loadQuote(quote);
                setShowAdminModal(false);
            }}
        />;
    }

    return (
        <div className="omntec-container">
            <NavBar 
                handleLogout={handleLogout}
                onShowAIAssistant={() => setAiModalOpen(true)}
                onShowDashboard={handleBackToQuotesList}
                onShowAdmin={handleShowAdmin}
                user={user}
                quotes={quotes}
                dropdownSearchTerm={dropdownSearchTerm}
                setDropdownSearchTerm={setDropdownSearchTerm}
                isDropdownOpen={isDropdownOpen}
                setIsDropdownOpen={setIsDropdownOpen}
                loadQuote={loadQuote}
                showAdminModal={showAdminModal}
                
            />
            
            <div className='logoLight'>
                <img src="/Logo-P.png" alt="Omntec Logo" style={{ maxWidth: '100%', height: 'auto', width: '450px', maxWidth: '90vw' }}/>
            </div>

            {!recommendation && (
                <div className="section-name">
                    <h2>{initialStep ? 'Determining PROTEUS model...' : `Configuring your PROTEUS ${initialRecommendation}`}</h2>
                </div>
            )}

            {!recommendation && <ProgressBar progress={progress} />}

            {recommendation ? (
                <Recommendation
                    recommendation={recommendation}
                    userInfo={userInfo}
                    answers={answers}
                    questions={Questions.questions}
                    handlePrint={() => window.print()}
                    resetQuoteGenerator={resetQuoteGenerator}
                    currentQuoteNumber={currentQuoteNumber}
                    backToQuotesList={handleBackToQuotesList} 
                    totalPrice={getTotalPrice().totalPrice}
                    items={getTotalPrice().items}
                    setAnswers={setAnswers}
                    setRecommendation={setRecommendation}
                    user={user}
                    getTotalPrice={getTotalPrice}
                    createdAt={quoteCreatedAt}
                    userDiscount={userDiscount}
                    setUserDiscount={setUserDiscount}
                />
            ) : hardStopMessage ? (
                <div className="hard-stop-container" style={{
                    backgroundColor: 'rgb(255, 243, 205)',
                    border: '2px solid rgb(255, 193, 7)',
                    borderRadius: '12px',
                    padding: '40px',
                    margin: '20px auto',
                    maxWidth: '600px',
                    textAlign: 'center',
                    boxShadow: '0 4px 12px rgba(33, 150, 243, 0.15)'
                }}>
                    <div style={{ 
                        display: 'flex', 
                        alignItems: 'center', 
                        justifyContent: 'center',
                        marginBottom: '20px',
                        gap: '12px'
                    }}>
                        <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <circle cx="12" cy="12" r="10" fill="#2196f3"/>
                            <path d="M12 7V9" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M12 11V17" stroke="white" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                        <h2 style={{ 
                            color: '#1565c0', 
                            margin: 0,
                            fontSize: '1.5em',
                            fontWeight: '600'
                        }}>
                            Your configuration requires a Proteus-X system
                        </h2>
                    </div>
                    <p style={{ 
                        fontSize: '1.1em', 
                        color: '#1976d2',
                        lineHeight: '1.7',
                        marginBottom: '25px'
                    }}>
                        The Proteus-X quote generator will be available shortly. In the meantime, please contact your OMNTEC sales representative or call <strong>+1 (877) 814-2001</strong> for assistance.
                    </p>
                    <button 
                        className="button" 
                        onClick={() => {
                            setHardStopMessage(null);
                            // Allow them to go back and change their answer
                        }}
                        style={{ marginTop: '10px' }}
                    >
                        Go Back and Modify Selection
                    </button>
                </div>
            ) : (
                <Question
                    questionObj={currentQuestionObj}
                    answers={answers}
                    handleInputChange={handleInputChange}
                    handleKeyUp={handleKeyUp}
                    tankCount={tankCount}
                    initialRecommendation={initialRecommendation}
                />
            )}
            
            {!recommendation && !hardStopMessage && (
                <div className="navigation-buttons">
                    <button className="button" onClick={handlePreviousQuestion} disabled={currentSectionIndex === 0 && currentQuestionIndex === 0}>Previous</button>
                    <button 
                        className="button" 
                        onClick={handleNextQuestion} 
                        disabled={!isCurrentQuestionAnswered()}
                        title={!isCurrentQuestionAnswered() ? "Please answer the current question before proceeding" : ""}
                    >
                        Next
                    </button>
                </div>
            )}

            <AIModal isOpen={aiModalOpen} onClose={() => setAiModalOpen(false)}>
                <AIAssistant onClose={() => setAiModalOpen(false)} />
            </AIModal>
        </div>
    );
}

export default OmntecQuoteGenerator;
